import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { SILENT_TOKEN_CACHE_LOOKUP_POLICY } from '../constants/msal-silent-request';

export interface WorkerUploadProgress {
  loaded: number;
  total: number;
  progress: number;
  speed?: number;
  eta?: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkerUploadService {
  private msalService = inject(MsalService);

  private workers = new Map<string, Worker>();

  constructor() {}

  /**
   * Get access token from MSAL
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (!activeAccount) {
        return null;
      }

      const tokenRequest = {
        scopes: environment.apiConfig.scopes || [],
        account: activeAccount,
        cacheLookupPolicy: SILENT_TOKEN_CACHE_LOOKUP_POLICY,
      };

      const response = await this.msalService.instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      console.error('[WorkerUploadService] Error getting access token:', error);
      return null;
    }
  }

  /**
   * Extract file data from FormData
   */
  private async extractFileFromFormData(formData: FormData): Promise<{
    file: File | null;
    additionalData: { [key: string]: string };
  }> {
    let file: File | null = null;
    const additionalData: { [key: string]: string } = {};

    // Use FormData.get() to extract known fields
    // Since we control the FormData creation, we know it has 'file' and 'id'
    const fileValue = formData.get('file');
    if (fileValue instanceof File) {
      file = fileValue;
    }

    const idValue = formData.get('id');
    if (idValue && typeof idValue === 'string') {
      additionalData['id'] = idValue;
    }

    return { file, additionalData };
  }

  /**
   * Convert File to ArrayBuffer
   */
  private async fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Upload using Web Worker for true background processing
   */
  async uploadWithWorker(
    uploadId: string,
    url: string,
    formData: FormData,
    onProgress?: (progress: WorkerUploadProgress) => void
  ): Promise<unknown> {
    const authToken = await this.getAccessToken();
    if (!authToken) {
      throw new Error('Authentication required');
    }

    // Extract file and additional data from FormData
    const { file, additionalData } = await this.extractFileFromFormData(formData);
    if (!file) {
      throw new Error('No file found in FormData');
    }

    // Convert file to ArrayBuffer for transfer to worker
    const fileData = await this.fileToArrayBuffer(file);

    return new Promise((resolve, reject) => {
      try {
        // Create a new worker for this upload
        const worker = new Worker(new URL('../workers/upload.worker', import.meta.url), {
          type: 'module',
        });

        this.workers.set(uploadId, worker);

        // Handle messages from worker
        worker.onmessage = ({ data }) => {
          switch (data.type) {
            case 'progress':
              if (onProgress) {
                onProgress({
                  loaded: data.loaded,
                  total: data.total,
                  progress: data.progress,
                  speed: data.speed,
                  eta: data.eta,
                });
              }
              break;

            case 'complete':
              this.workers.delete(uploadId);
              worker.terminate();
              resolve(data.response);
              break;

            case 'error':
              this.workers.delete(uploadId);
              worker.terminate();
              reject(new Error(data.error));
              break;
          }
        };

        // Handle worker errors
        worker.onerror = (error) => {
          console.error('[WorkerUploadService] Worker error:', error);
          this.workers.delete(uploadId);
          worker.terminate();
          reject(error);
        };

        // Send upload message to worker with ArrayBuffer (transferable)
        const message = {
          type: 'upload',
          url,
          fileData,
          fileName: file.name,
          fileType: file.type,
          additionalData,
          token: authToken,
        };

        // Transfer the ArrayBuffer to the worker (zero-copy)
        worker.postMessage(message, [fileData]);
      } catch (error) {
        console.error('[WorkerUploadService] Failed to create worker:', error);
        this.workers.delete(uploadId);
        reject(error);
      }
    });
  }

  /**
   * Upload with Blob directly (alternative method)
   */
  async uploadWithBlob(
    uploadId: string,
    url: string,
    blob: Blob,
    fileName: string,
    additionalData?: { [key: string]: string },
    onProgress?: (progress: WorkerUploadProgress) => void
  ): Promise<unknown> {
    const authToken = await this.getAccessToken();
    if (!authToken) {
      throw new Error('Authentication required');
    }

    // Convert Blob to ArrayBuffer
    const fileData = await blob.arrayBuffer();

    return new Promise((resolve, reject) => {
      try {
        const worker = new Worker(new URL('../workers/upload.worker', import.meta.url), {
          type: 'module',
        });

        this.workers.set(uploadId, worker);

        worker.onmessage = ({ data }) => {
          switch (data.type) {
            case 'progress':
              if (onProgress) {
                onProgress({
                  loaded: data.loaded,
                  total: data.total,
                  progress: data.progress,
                  speed: data.speed,
                  eta: data.eta,
                });
              }
              break;

            case 'complete':
              this.workers.delete(uploadId);
              worker.terminate();
              resolve(data.response);
              break;

            case 'error':
              this.workers.delete(uploadId);
              worker.terminate();
              reject(new Error(data.error));
              break;
          }
        };

        worker.onerror = (error) => {
          console.error('[WorkerUploadService] Worker error:', error);
          this.workers.delete(uploadId);
          worker.terminate();
          reject(error);
        };

        const message = {
          type: 'upload',
          url,
          fileData,
          fileName,
          fileType: blob.type,
          additionalData,
          token: authToken,
        };

        // Transfer the ArrayBuffer
        worker.postMessage(message, [fileData]);
      } catch (error) {
        console.error('[WorkerUploadService] Failed to create worker:', error);
        this.workers.delete(uploadId);
        reject(error);
      }
    });
  }

  /**
   * Upload JSON data using Web Worker
   */
  async uploadJson(
    uploadId: string,
    url: string,
    data: unknown,
    method: 'POST' | 'PUT' | 'PATCH' = 'POST',
    onProgress?: (progress: WorkerUploadProgress) => void
  ): Promise<unknown> {
    const authToken = await this.getAccessToken();
    if (!authToken) {
      throw new Error('Authentication required');
    }

    // Convert JSON to string then to ArrayBuffer for transfer
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const jsonData = encoder.encode(jsonString).buffer;

    return new Promise((resolve, reject) => {
      try {
        const worker = new Worker(new URL('../workers/upload.worker', import.meta.url), {
          type: 'module',
        });

        this.workers.set(uploadId, worker);

        worker.onmessage = ({ data }) => {
          switch (data.type) {
            case 'progress':
              if (onProgress) {
                onProgress({
                  loaded: data.loaded,
                  total: data.total,
                  progress: data.progress,
                  speed: data.speed,
                  eta: data.eta,
                });
              }
              break;

            case 'complete':
              this.workers.delete(uploadId);
              worker.terminate();
              resolve(data.response);
              break;

            case 'error':
              this.workers.delete(uploadId);
              worker.terminate();
              reject(new Error(data.error));
              break;
          }
        };

        worker.onerror = (error) => {
          console.error('[WorkerUploadService] Worker error:', error);
          this.workers.delete(uploadId);
          worker.terminate();
          reject(error);
        };

        const message = {
          type: 'upload-json',
          url,
          method,
          jsonData,
          token: authToken,
        };

        // Transfer the ArrayBuffer
        worker.postMessage(message, [jsonData]);
      } catch (error) {
        console.error('[WorkerUploadService] Failed to create worker:', error);
        this.workers.delete(uploadId);
        reject(error);
      }
    });
  }

  /**
   * Cancel an ongoing upload
   */
  cancelUpload(uploadId: string): void {
    const worker = this.workers.get(uploadId);
    if (worker) {
      worker.terminate();
      this.workers.delete(uploadId);
    }
  }

  /**
   * Clean up all workers
   */
  cleanup(): void {
    this.workers.forEach((worker) => {
      worker.terminate();
    });
    this.workers.clear();
  }
}
